import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getAllClients } from '../utils/apiService';
import { calculateClientCompletion, getCompletionBadge } from '../utils/completion/clientCompletionCalculator';
import ServerCompletionDetails from '../components/completion/ServerCompletionDetails';

const ClientsList = () => {
  const [clients, setClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name'); // 'name', 'servers', 'recent', 'completion'
  const [expandedClients, setExpandedClients] = useState(new Set());
  const [showCompletionDetails, setShowCompletionDetails] = useState(new Set());
  const [filterByCompletion, setFilterByCompletion] = useState('all'); // 'all', 'complete', 'incomplete', 'alerts'

  useEffect(() => {
    // Fonction pour charger les clients depuis l'API
    const loadClients = async () => {
      const data = await getAllClients();
      setClients(data);
    };
    
    loadClients();
  }, []);

  // Fonction pour normaliser le nom du client (enlever QUAL, etc.)
  const normalizeClientName = (name) => {
    return name
      .replace(/\s*QUAL\s*/gi, '')
      .replace(/\s*QUALIFICATION\s*/gi, '')
      .replace(/\s*PRODUCTION\s*/gi, '')
      .trim();
  };

  // Fonction pour déterminer si un client est en production ou qualification
  const isProduction = (client) => {
    return client.environment?.toLowerCase().includes('production') || 
           (!client.environment?.toLowerCase().includes('qualification') && client.name && !client.name.toLowerCase().includes('qual'));
  };

  const isQualification = (client) => {
    return client.environment?.toLowerCase().includes('qualification') || 
           client.name?.toLowerCase().includes('qual');
  };

  // Regrouper les clients par nom de base
  const groupClientsByBaseName = () => {
    const groups = {};
    
    clients.forEach(client => {
      const baseName = normalizeClientName(client.name);
      
      if (!groups[baseName]) {
        groups[baseName] = {
          baseName,
          environments: []
        };
      }
      
      // Déterminer l'environnement du client
      let envType = 'other';
      if (isProduction(client) && !isQualification(client)) {
        envType = 'production';
      } else if (isQualification(client) && !isProduction(client)) {
        envType = 'qualification';
      } else if (isProduction(client) && isQualification(client)) {
        envType = 'both';
      }
      
      groups[baseName].environments.push({
        ...client,
        envType
      });
    });
    
    return groups;
  };

  const clientGroups = groupClientsByBaseName();

  // Filtrage des groupes de clients
  const filteredGroups = Object.entries(clientGroups).filter(([baseName, group]) => {
    // Filtre de recherche
    const matchesSearch = baseName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         group.environments.some(env => env.description?.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesSearch;
  });

  // Calculer la complétion pour chaque groupe
  const groupsWithCompletion = filteredGroups.map(([baseName, group]) => {
    // Calculer la complétion moyenne du groupe
    const completions = group.environments.map(env => calculateClientCompletion(env));
    const avgCompletion = Math.round(
      completions.reduce((sum, c) => sum + c.percentage, 0) / completions.length
    );
    const hasAlerts = completions.some(c => c.details.hasNoProbe || c.details.hasUnoptimizedDB);
    
    return [baseName, { ...group, avgCompletion, hasAlerts, completions }];
  });

  // Filtrer par complétion
  const filteredByCompletion = groupsWithCompletion.filter(([baseName, group]) => {
    switch (filterByCompletion) {
      case 'complete':
        return group.avgCompletion === 100;
      case 'incomplete':
        return group.avgCompletion < 100;
      case 'alerts':
        return group.hasAlerts;
      default:
        return true;
    }
  });

  // Tri des groupes
  const sortedGroups = [...filteredByCompletion].sort(([nameA, groupA], [nameB, groupB]) => {
    switch (sortBy) {
      case 'name':
        return nameA.localeCompare(nameB);
      case 'servers':
        const serversA = groupA.environments.reduce((sum, env) => sum + (env.servers?.length || 0), 0);
        const serversB = groupB.environments.reduce((sum, env) => sum + (env.servers?.length || 0), 0);
        return serversB - serversA;
      case 'recent':
        const dateA = Math.max(...groupA.environments.map(env => new Date(env.updatedAt || env.createdAt).getTime()));
        const dateB = Math.max(...groupB.environments.map(env => new Date(env.updatedAt || env.createdAt).getTime()));
        return dateB - dateA;
      case 'completion':
        return groupB.avgCompletion - groupA.avgCompletion;
      default:
        return 0;
    }
  });

  // Statistiques
  const totalProduction = clients.filter(isProduction).length;
  const totalQualification = clients.filter(isQualification).length;

  // Toggle expansion d'un client
  const toggleClientExpansion = (baseName) => {
    const newExpanded = new Set(expandedClients);
    if (newExpanded.has(baseName)) {
      newExpanded.delete(baseName);
    } else {
      newExpanded.add(baseName);
    }
    setExpandedClients(newExpanded);
  };

  // Fonction pour obtenir la couleur selon l'environnement
  const getEnvColor = (envType) => {
    switch (envType) {
      case 'production':
        return 'bg-red-500 text-white';
      case 'qualification':
        return 'bg-orange-500 text-white';
      case 'both':
        return 'bg-purple-500 text-white';
      default:
        return 'bg-gray-400 text-white';
    }
  };

  // Fonction pour obtenir la dernière mise à jour d'un environnement
  const getLastUpdate = (env) => {
    if (!env.updates || env.updates.length === 0) return null;
    // Les mises à jour sont stockées dans l'ordre chronologique, prendre la dernière
    return env.updates[env.updates.length - 1];
  };

  // Fonction pour obtenir le label de l'environnement
  const getEnvLabel = (env) => {
    if (env.envType === 'both') {
      return 'PROD/QUAL';
    } else if (env.envType === 'production') {
      return 'PROD';
    } else if (env.envType === 'qualification') {
      return 'QUAL';
    }
    return env.environment || 'Non spécifié';
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Liste des clients</h1>
        <div className="flex items-center gap-4">
          {/* Statistiques */}
          <div className="flex items-center gap-2 text-sm">
            <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full font-medium">
              {totalProduction} PROD
            </span>
            <span className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full font-medium">
              {totalQualification} QUAL
            </span>
            <span className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full font-medium">
              {Object.keys(clientGroups).length} Clients uniques
            </span>
          </div>
          
          {/* Actions principales */}
          <div className="flex items-center gap-2">
            <Link 
              to="/completion-dashboard" 
              className="text-gray-600 hover:text-gray-800 p-2 rounded-lg hover:bg-gray-100 transition-colors flex items-center text-sm"
              title="Tableau de bord de complétion"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              Complétion
            </Link>
            
            <Link 
              to="/admin" 
              className="text-gray-600 hover:text-gray-800 p-2 rounded-lg hover:bg-gray-100 transition-colors flex items-center text-sm"
              title="Administration"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Admin
            </Link>
            
            <Link 
              to="/add-client" 
              className="hoppen-btn px-4 py-2 rounded-lg shadow hover:shadow-md transition-all flex items-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Ajouter un client
            </Link>
          </div>
        </div>
      </div>

      {/* Barre de filtres et recherche */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6 space-y-4">
        {/* Recherche */}
        <div>
          <input
            type="text"
            placeholder="Rechercher un client..."
            className="w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Filtre et tri */}
        <div className="flex flex-wrap gap-4">
          {/* Tri */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">Trier par:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="name">Nom</option>
              <option value="servers">Nombre de serveurs</option>
              <option value="recent">Modification récente</option>
              <option value="completion">Taux de complétion</option>
            </select>
          </div>

          {/* Filtre par complétion */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">Filtrer:</span>
            <select
              value={filterByCompletion}
              onChange={(e) => setFilterByCompletion(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Tous les clients</option>
              <option value="complete">✅ Complets uniquement</option>
              <option value="incomplete">⚠️ Incomplets</option>
              <option value="alerts">🔴 Avec alertes (sonde/BDD)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Résultats filtrés */}
      {sortedGroups.length > 0 && (
        <div className="mb-4 text-sm text-gray-600">
          {sortedGroups.length} client{sortedGroups.length > 1 ? 's' : ''} trouvé{sortedGroups.length > 1 ? 's' : ''}
        </div>
      )}

      {/* Liste des groupes de clients */}
      <div className="space-y-6">
        {sortedGroups.map(([baseName, group]) => {
          const isExpanded = expandedClients.has(baseName);
          const prodEnv = group.environments.find(env => env.envType === 'production' || env.envType === 'both');
          const qualEnv = group.environments.find(env => env.envType === 'qualification' || (env.envType === 'both' && env !== prodEnv));
          
          return (
            <div key={baseName} className="bg-white rounded-lg shadow-md overflow-hidden">
              {/* En-tête du groupe client */}
              <div
                className="p-6 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => toggleClientExpansion(baseName)}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h2 className="text-xl font-semibold">{baseName}</h2>
                      <div className="flex gap-2">
                        {prodEnv && (
                          <span className="bg-red-500 text-white px-3 py-1 rounded-full text-xs font-bold uppercase">
                            PROD
                          </span>
                        )}
                        {qualEnv && (
                          <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-xs font-bold uppercase">
                            QUAL
                          </span>
                        )}
                      </div>
                    </div>
                    <p className="text-gray-600 text-sm">
                      {group.environments[0].description || 'Aucune description'}
                    </p>
                    <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                      <span>
                        {group.environments.reduce((sum, env) => sum + (env.servers?.length || 0), 0)} serveurs total
                      </span>
                      {group.environments.some(env => env.remoteMaintenance) && (
                        <span className="text-green-600">✓ Maintenance à distance</span>
                      )}
                    </div>
                  </div>
                  <div className="text-gray-400">
                    <svg 
                      className={`w-6 h-6 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Cartes des environnements (expandable) */}
              {isExpanded && (
                <div className="border-t border-gray-200 p-6 bg-gray-50">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {group.environments.map((env) => (
                      <Link
                        key={env.id}
                        to={`/client/${env.id}`}
                        className={`block bg-white rounded-lg p-4 border-2 hover:shadow-lg transition-all ${
                          env.envType === 'production' ? 'border-red-300 hover:border-red-500' :
                          env.envType === 'qualification' ? 'border-orange-300 hover:border-orange-500' :
                          env.envType === 'both' ? 'border-purple-300 hover:border-purple-500' :
                          'border-gray-300'
                        }`}
                      >
                        <div className="flex justify-between items-start mb-3">
                          <h3 className="font-semibold">{env.name}</h3>
                          <span className={`${getEnvColor(env.envType)} px-3 py-1 rounded-full text-xs font-bold`}>
                            {getEnvLabel(env)}
                          </span>
                        </div>

                        <div className="space-y-2 text-sm">
                          {/* URLs cliquables */}
                          {(env.patientPortalUrl || env.carePortalUrl) && (
                            <div className="space-y-1">
                              {env.patientPortalUrl && (
                                <div className="text-gray-600">
                                  <span className="font-medium">Patient:</span> 
                                  <a 
                                    href={env.patientPortalUrl} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="ml-1 text-xs text-blue-600 hover:text-blue-800 hover:underline truncate block"
                                  >
                                    {env.patientPortalUrl}
                                  </a>
                                </div>
                              )}
                              {env.carePortalUrl && (
                                <div className="text-gray-600">
                                  <span className="font-medium">Soignant:</span>
                                  <a 
                                    href={env.carePortalUrl} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="ml-1 text-xs text-blue-600 hover:text-blue-800 hover:underline truncate block"
                                  >
                                    {env.carePortalUrl}
                                  </a>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Serveurs */}
                          <div className="flex items-center gap-4 text-gray-500">
                            <span>
                              <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
                              </svg>
                              {env.servers?.length || 0} serveur{(env.servers?.length || 0) > 1 ? 's' : ''}
                            </span>
                          </div>

                          {/* Dernière mise à jour */}
                          {(() => {
                            const lastUpdate = getLastUpdate(env);
                            if (lastUpdate) {
                              return (
                                <div className="pt-2 border-t border-gray-200">
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs text-gray-500">Dernière mise à jour:</span>
                                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                                      lastUpdate.updateType === 'Production' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                                    }`}>
                                      {lastUpdate.updateType || 'MAJ'}
                                    </span>
                                  </div>
                                  <div className="mt-1">
                                    <div className="text-xs font-medium text-gray-700">{lastUpdate.title}</div>
                                    <div className="text-xs text-gray-500">
                                      {new Date(lastUpdate.date).toLocaleDateString('fr-FR')} - 
                                      {lastUpdate.commits?.manager || lastUpdate.commits?.patientApp || lastUpdate.commits?.marshallerFrontal || 'Version non spécifiée'}
                                    </div>
                                  </div>
                                </div>
                              );
                            }
                            return null;
                          })()}
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {sortedGroups.length === 0 && (
          <div className="text-center py-10 text-gray-500 bg-white rounded-lg">
            Aucun client trouvé. 
            {searchTerm && (
              <span className="block mt-2">
                Essayez de modifier vos critères de recherche ou{' '}
                <button
                  onClick={() => {
                    setSearchTerm('');
                  }}
                  className="text-blue-600 hover:underline"
                >
                  réinitialiser les filtres
                </button>
              </span>
            )}
            {!searchTerm && (
              <Link to="/add-client" className="text-blue-600 ml-1 hover:underline">
                Ajouter un nouveau client
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientsList;